import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Toast from './ui/Toast';

const ShareStoryPanel = ({ story, onUpdate, onChange }) => {
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  
  // This function now just notifies the parent component about the visibility change
  const handleVisibilityChange = (visibility) => {
    if (onChange) {
      onChange(visibility);
    }
  };

  const handleGenerateNewLink = async () => {
    try {
      setLoading(true);
      // Generate a unique UUID for the share link
      const shareId = crypto.randomUUID();
      // Update the story with the new share ID and ensure visibility is 'shared'
      onUpdate({
        ...story,
        share_id: shareId,
        visibility: 'shared'
      });
      setToast({
        show: true,
        message: 'New share link generated',
        type: 'success'
      });
    } catch (error) {
      setToast({
        show: true,
        message: 'Failed to generate new link',
        type: 'error'
      });
    } finally {
      setLoading(false);
      setTimeout(() => setToast({ show: false }), 3000);
    }
  };

  const handleRevokeLink = async () => {
    try {
      setLoading(true);
      // Update the story to revoke the share link but keep visibility as 'shared'
      // This way the user can still generate a new link without changing visibility
      onUpdate({
        ...story,
        share_id: null,
        visibility: 'shared' // Keep as shared so they can generate a new link
      });
      setToast({
        show: true,
        message: 'Share link revoked',
        type: 'success'
      });
    } catch (error) {
      setToast({
        show: true,
        message: 'Failed to revoke link',
        type: 'error'
      });
    } finally {
      setLoading(false);
      setTimeout(() => setToast({ show: false }), 3000);
    }
  };

  const copyShareLink = useCallback(() => {
    if (!story.share_id) return;
    
    // When using HashRouter, we need to use /#/shared/ format
    const shareUrl = `${window.location.origin}/#/shared/${story.share_id}`;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    
    setToast({
      show: true,
      message: 'Share link copied to clipboard',
      type: 'success'
    });
    setTimeout(() => setToast({ show: false }), 3000);
  }, [story.share_id]);

  return (
    <div className="bg-white rounded-xl p-4 shadow-soft">
      <h3 className="text-lg font-medium text-purple-700 mb-4">Sharing Settings</h3>
      <div className="space-y-4">
        {/* Private Option */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => handleVisibilityChange('private')}
          className={`w-full p-3 rounded-lg border-2 transition-all flex items-center gap-3 ${
            story.visibility === 'private'
              ? 'border-purple-500 bg-purple-50'
              : 'border-gray-200 hover:border-purple-300'
          }`}
          disabled={loading}
          type="button"
        >
          <SafeIcon
            icon={FiIcons.FiLock}
            className={`${story.visibility === 'private' ? 'text-purple-600' : 'text-gray-500'}`}
          />
          <div className="text-left">
            <div className="font-medium">Private</div>
            <div className="text-sm text-gray-500">Only you can see this story</div>
          </div>
          {story.visibility === 'private' && (
            <SafeIcon icon={FiIcons.FiCheck} className="ml-auto text-purple-600" />
          )}
        </motion.button>

        {/* Share with Link Option */}
        <div className={`w-full p-3 rounded-lg border-2 transition-all ${
          story.visibility === 'shared'
            ? 'border-purple-500 bg-purple-50'
            : 'border-gray-200'
        }`}>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => handleVisibilityChange('shared')}
            className="w-full flex items-center gap-3"
            disabled={loading}
            type="button"
          >
            <SafeIcon
              icon={FiIcons.FiLink}
              className={`${story.visibility === 'shared' ? 'text-purple-600' : 'text-gray-500'}`}
            />
            <div className="text-left flex-1">
              <div className="font-medium">Share with Link</div>
              <div className="text-sm text-gray-500">Anyone with the link can view</div>
            </div>
            {story.visibility === 'shared' && (
              <SafeIcon icon={FiIcons.FiCheck} className="text-purple-600" />
            )}
          </motion.button>
          
          {/* Share Link Controls - Show when visibility is 'shared' */}
          {story.visibility === 'shared' && (
            <div className="mt-3 border-t border-purple-100 pt-3">
              {/* Show link input and copy button only if share_id exists */}
              {story.share_id ? (
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="text"
                    value={`${window.location.origin}/#/shared/${story.share_id}`}
                    readOnly
                    className="flex-1 px-3 py-1 text-sm bg-purple-50 border border-purple-200 rounded-lg"
                  />
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={copyShareLink}
                    className="p-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg"
                    type="button"
                    title="Copy link"
                  >
                    <SafeIcon icon={copied ? FiIcons.FiCheck : FiIcons.FiCopy} />
                  </motion.button>
                </div>
              ) : (
                <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-700">
                    No active share link. Generate one to share this story.
                  </p>
                </div>
              )}
              
              <div className="flex gap-2">
                {/* Generate/Regenerate Link Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleGenerateNewLink}
                  className="text-xs px-3 py-1 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg flex items-center gap-1"
                  type="button"
                  disabled={loading}
                >
                  <SafeIcon icon={FiIcons.FiRefreshCw} className="text-xs" />
                  {story.share_id ? 'Generate New Link' : 'Generate Link'}
                </motion.button>
                
                {/* Revoke Link Button - Only show if there's an active link */}
                {story.share_id && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleRevokeLink}
                    className="text-xs px-3 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg flex items-center gap-1"
                    type="button"
                    disabled={loading}
                  >
                    <SafeIcon icon={FiIcons.FiX} className="text-xs" /> Revoke Link
                  </motion.button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Public Option */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => handleVisibilityChange('public')}
          className={`w-full p-3 rounded-lg border-2 transition-all flex items-center gap-3 ${
            story.visibility === 'public'
              ? 'border-purple-500 bg-purple-50'
              : 'border-gray-200 hover:border-purple-300'
          }`}
          disabled={loading}
          type="button"
        >
          <SafeIcon
            icon={FiIcons.FiGlobe}
            className={`${story.visibility === 'public' ? 'text-purple-600' : 'text-gray-500'}`}
          />
          <div className="text-left">
            <div className="font-medium">Public</div>
            <div className="text-sm text-gray-500">Anyone can discover this story</div>
          </div>
          {story.visibility === 'public' && (
            <SafeIcon icon={FiIcons.FiCheck} className="ml-auto text-purple-600" />
          )}
        </motion.button>
      </div>

      {toast.show && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
      )}
    </div>
  );
};

export default ShareStoryPanel;